import { useState, useEffect, useRef, useCallback } from "react";
import axios from 'axios'; // Import Axios
import { RiPencilLine, RiDeleteBinLine, RiAddLine, RiFileTextLine, RiErrorWarningLine } from 'react-icons/ri';
import { BsInstagram, BsYoutube, BsTiktok, BsFacebook, BsThreeDotsVertical, BsTwitterX, BsThreads, BsLinkedin, BsReddit, BsPinterest } from 'react-icons/bs';
import { isMobile as checkIsMobile, handleResize, debounce } from '../../utils/utils'; // Import utility functions
import { getFileType } from "../../utils/getFileType";
import Pagination from "../Tools/Pagination";
import { Modal } from "../Modal/Modal";
import ContentLoader from 'react-content-loader';
import { formatCustomDateTime } from "../../utils/date";
import DropdownButton from "../Tools/DropdownButton";
import { useWorkspace } from '../../context/WorkspaceContext';
import { Button } from "../../ui/button";
import useThemeColors from '../../hooks/useThemeColors';
import { FaCopy } from "react-icons/fa";

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const Drafts = ({ onEditDraft }) => {
    const [drafts, setDrafts] = useState([]);
    const [activeTab, setActiveTab] = useState('draft'); // Initialize the active tab
    const [currentPage, setCurrentPage] = useState(1);
    const [tabs, setTabs] = useState([
        { id: 'draft', label: 'Drafts', count: { length: 0, color: 'bg-blue-500' } },
        { id: 'scheduled', label: 'Scheduled', count: { length: 0, color: 'bg-yellow-500' } },
        { id: 'uploaded', label: 'History', count: { length: 0, color: 'bg-green-500' } },
    ]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [modalGroup, setModalGroup] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const hasRunOnceRef = useRef(false);
    const [isMobile, setIsMobile] = useState(checkIsMobile());
    const { activeWorkspace, idToken } = useWorkspace();

    useEffect(() => {
        const debouncedHandleResize = debounce(() => handleResize(setIsMobile), 100);
        window.addEventListener("resize", debouncedHandleResize);
        return () => {
            window.removeEventListener("resize", debouncedHandleResize);
        };
    }, [isMobile]);

    useEffect(() => {
        const getDrafts = async () => {
            if (!idToken) return;
            try {
                const response = await axios.get(BACKEND_API_URL + '/api/auth/user/drafts', {
                    params: { workspaceId: activeWorkspace.id },
                    headers: { Authorization: `Bearer ${idToken}` },
                });
                const arr = response.data;
                setDrafts(arr);
                await updateTabCount(arr);
            } catch (error) {
                console.error(error);
            }
            finally {
                setIsLoading(false);
            }
        };

        if (!hasRunOnceRef.current && isDeleteModalOpen === false) {
            hasRunOnceRef.current = true;
            getDrafts();
        }

        if (isDeleteModalOpen === true || activeWorkspace) {
            hasRunOnceRef.current = false;
        }
    }, [isDeleteModalOpen, activeWorkspace, idToken]);

    const updateTabCount = async (mergedData) => {
        if (mergedData.length <= 0) return;

        const tabCounts = {
            draft: 0,
            scheduled: 0,
            failed: 0,
            uploaded: 0
        };
        
        mergedData.forEach(group => {
            // Count based on post status within each group
            group.posts.forEach(post => {
                if (post.status === 'draft') {
                    tabCounts.draft++;
                } else if (post.status === 'failed') {
                    tabCounts.failed++;
                } else if (post.status === 'scheduled') {
                    tabCounts.scheduled++;
                } else if (post.status === 'uploaded') {
                    tabCounts.uploaded++;
                }
            });
        });

        const hasFailedDrafts = tabCounts.failed > 0;

        setTabs(prevTabs => {
            const updatedTabs = [...prevTabs];

            if (!hasFailedDrafts) {
                setActiveTab('draft');
            } else {
                setActiveTab('failed');
                // Check if the "Failed" tab doesn't exist, add it as the first index
                if (!updatedTabs.find(tab => tab.id === 'failed')) {
                    updatedTabs.unshift({ id: 'failed', label: 'Failed', count: { length: 0, color: 'bg-red-500' } });
                }
            }

            return updatedTabs.map(tab => {
                if (tab.id in tabCounts) {
                    return { ...tab, count: { ...tab.count, length: tabCounts[tab.id] } };
                }
                return tab;
            });
        });
    };

    const handleTabChange = (newTab) => {
        setActiveTab(newTab); // Update the active tab when a tab is changed
        setCurrentPage(1); // Reset current page when tab changes
    };

    const paginate = useCallback(() => {
        if (!drafts || drafts.length <= 0) return { currentItems: [], maxPage: 0 };
        const itemsPerPage = 3;
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;

        // Filter and sort drafts based on the latest updatedAt of their posts
        let sortedDrafts = drafts
            .map(group => {
                const latestPost = group.posts.reduce((latest, post) => {
                    return new Date(post.updatedAt) > new Date(latest.updatedAt) ? post : latest;
                }, group.posts[0]);
                return { ...group, latestUpdatedAt: new Date(latestPost.updatedAt) };
            })
            .sort((a, b) => b.latestUpdatedAt - a.latestUpdatedAt);

        // Filter drafts based on the active tab
        let filteredDrafts = sortedDrafts.map(group => ({
            ...group,
            posts: group.posts.filter(post => post.status === activeTab)
        })).filter(group => group.posts.length > 0);

        const maxPage = Math.ceil(filteredDrafts.length / itemsPerPage);
        
        // Get the current items for the page - no need for additional filtering
        const currentItems = filteredDrafts.slice(indexOfFirstItem, indexOfLastItem);

        return { currentItems, maxPage };
    }, [drafts, activeTab, currentPage]);

    useEffect(() => {
        if (drafts.length > 0) {
            const { currentItems, maxPage } = paginate();
            setCurrentItems(currentItems);
            setMaxPage(maxPage);
        } else {
            setCurrentItems([]);
            setMaxPage(1);
        }
    }, [drafts, activeTab, currentPage, paginate]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= paginate().maxPage) {
            setCurrentPage(newPage);
        }
    };

    const [currentItems, setCurrentItems] = useState([]);
    const [maxPage, setMaxPage] = useState(1);

    const handleDeleteButtonClick = (group) => {
        setModalGroup(group);
        setIsDeleteModalOpen(true);
    };


    const closeModal = () => {
        setModalGroup(null);
        setIsDeleteModalOpen(false);
    };

    return (
        <div className="">
            {/* Tabs */}
            <div className={`grid w-full mb-4 ${tabs.length === 2 ? 'grid-cols-2' : tabs.length === 3 ? 'grid-cols-3' : tabs.length === 4 ? 'grid-cols-4' : 'grid-cols-2'}`}>
                {
                    tabs.map((tab) => (
                        <Button
                            key={tab.id}
                            variant={activeTab === tab.id ? 'primary' : 'outline'}
                            onClick={() => handleTabChange(tab.id)}
                        >
                            {getIconForTab(tab.id)}
                            <span className={`${activeTab === tab.id ? 'nav-link' : '!text-gray-500'}`}>{tab.label}</span>
                            {tab.count && (
                                <span
                                    className={`px-2 rounded-full text-xs ${tab.count.color} text-white`}
                                >
                                    {tab.count.length}
                                </span>
                            )}
                        </Button>
                    ))
                }
            </div >

            {/* Tab Content */}
            <div>
                {
                isLoading ? (
                        <>
                            <Loader isMobile={isMobile} />
                        </>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 font-medium w-1/6">Status</th>
                                        <th className="px-4 py-2 font-medium w-3/6">Content</th>
                                        <th className="px-4 py-2 font-medium w-1/6">Media</th>
                                        <th className="px-4 py-2 font-medium w-1/6">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {currentItems && currentItems.length > 0 ? (
                                        currentItems.map((group) => (
                                            <TableRow
                                                key={group.post_id}
                                                group={group}
                                                onEditDraft={onEditDraft}
                                                handleDeleteButtonClick={handleDeleteButtonClick}
                                                activeTab={activeTab}
                                            />
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="py-5">
                                                <div className="flex flex-col items-center justify-center space-y-2">
                                                    <div className="p-4 rounded-full bg-gray-100">
                                                        <RiFileTextLine className="md:h-12 md:w-12 h-6 w-6 text-gray-500" />
                                                    </div>
                                                    <h3 className="text-md md:text-lg font-medium text-gray-900">No {activeTab} posts yet</h3>
                                                    <p className="text-sm md:text-base text-gray-500">Create your first draft to get started</p>
                                                    <Button 
                                                        size="md"
                                                        onClick={() => onEditDraft(null)}
                                                        className="mt-2 inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                    >
                                                        <RiAddLine className="h-5 w-5 mr-2" />
                                                        Create Draft
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
            </div >

            {/* Pagination */}
            {
                maxPage > 1 && (
                    <div className="flex justify-end mt-4">
                        <Pagination
                            currentPage={currentPage}
                            maxPage={maxPage}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )
            }

            {
                isDeleteModalOpen && (
                    <Modal open={isDeleteModalOpen} closeModal={closeModal} type={'VerifyDelete'} otherInfo={modalGroup} />
                )
            }
        </div >
    );
};

const TableRow = ({ group, onEditDraft, handleDeleteButtonClick, activeTab }) => {
    const post = group.posts[0];

    const handleDuplicateButtonClick = (group) => {
        // Skip if group is invalid
        if (!group) return;
        
        // Create duplicate data in the same format as Week.js
        const duplicateData = { ...group };
        delete duplicateData.post_id;
        
        if (duplicateData.posts && Array.isArray(duplicateData.posts)) {
            duplicateData.posts = duplicateData.posts.map(post => {
                const newPost = { ...post };
                delete newPost.post_id;
                delete newPost._id;
                return newPost;
            });
        }
        
        onEditDraft(duplicateData);
    };

    return (
        <tr>
            <td className="px-4 py-2 w-1/6">
                <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                        {group.posts.map((groupPost, index) => (
                            <div key={index} className="flex gap-1">
                                <IconForSocialMedia socialMedia={groupPost.uploadTo} />
                            </div>
                        ))}
                        <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium uppercase 
                  ${post.status === 'failed' ? 'bg-red-100 text-red-800' :
                                    post.status === 'draft' ? 'bg-blue-100 text-blue-800' :
                                        post.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-green-100 text-green-800'}`}
                        >
                            {post.status}
                        </span>
                    </div>
                    {['scheduled', 'uploaded', 'failed'].includes(post.status) && (
                        <div className="text-xs text-gray-500 mt-1 ml-1">
                            {post.status === 'scheduled' && (
                                <p>{formatCustomDateTime(new Date(post.schedule))}</p>
                            )}
                            {post.status === 'uploaded' && (
                                <p>{formatCustomDateTime(post.updatedAt)}</p>
                            )}
                            {post.status === 'failed' && (
                                <p>{formatCustomDateTime(post.updatedAt)}</p>
                            )}
                        </div>
                    )}
                    {/* Display failure reason if status is failed */}
                    {post.status === 'failed' && post.fail_reason && (
                        <div className="mt-2 ml-1 p-1.5 bg-red-50 rounded text-xs text-red-700 flex items-start">
                            <RiErrorWarningLine className="text-red-500 mt-0.5 mr-1 flex-shrink-0" />
                            <span>{post.fail_reason}</span>
                        </div>
                    )}
                </div>
            </td>
            <td className="px-4 py-2 w-3/6">
                <p className="font-semibold">{post.title}</p>
                {post.description?.length > 300 ? post.description.substring(0, 200) + "..." : post.description}
            </td>
            <td className="px-4 py-2 w-1/6">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                    <img
                        src={
                            getFileType(post.uploadType) === 'video'
                                ? post.thumbnail && post.thumbnail !== '' ? post.thumbnail : `${process.env.PUBLIC_URL}/no-thumbnail.png`
                                : post.firebase_path && post.firebase_path !== '' ? post.firebase_path : `${process.env.PUBLIC_URL}/no-thumbnail.png`
                        }
                        alt={post.title}
                        className="w-full h-full object-cover"
                    />
                </div>
            </td>
            <td className="px-4 py-2 w-1/6">
                <div className="flex space-x-2">
                    <DropdownButton label={<BsThreeDotsVertical size={18} />} classes="!p-0 hover:bg-none" options={[
                        { label: `Edit`, onClick: () => onEditDraft(group), icon: <RiPencilLine size={20} className="ml-5 text-blue-600 font-bold" /> },
                        { label: `Duplicate`, onClick: () => handleDuplicateButtonClick(group), icon: <FaCopy size={20} className="ml-5 text-blue-600 font-bold" /> },
                        {
                            label: `Delete`,
                            onClick: () => handleDeleteButtonClick(group),
                                    icon: <RiDeleteBinLine size={20} className="ml-5 text-blue-600 font-bold" />,
                                },
                    ]} />
                </div>
            </td>
        </tr>
    );
};

const Loader = ({ isMobile }) => {
    const { isDark } = useThemeColors();
    
    return (
        <ContentLoader
            speed={2}
            width="100%"
            height={isMobile ? 80 : 120}
            viewBox={isMobile ? "0 0 360 80" : "0 0 800 120"}
            backgroundColor={isDark ? "#2a2a2a" : "#f3f3f3"}
            foregroundColor={isDark ? "#3a3a3a" : "#ecebeb"}
        >
        {isMobile ? (
            <>
                {/* Mobile Layout - horizontally aligned */}
                {/* Status */}
                <rect x="10" y="20" rx="20" ry="20" width="60" height="20" />
                
                {/* Content (condensed) */}
                <rect x="80" y="15" rx="3" ry="3" width="120" height="12" />
                <rect x="80" y="35" rx="3" ry="3" width="100" height="10" />
                
                {/* Media Thumbnail */}
                <rect x="220" y="10" rx="6" ry="6" width="50" height="50" />
                
                {/* Actions */}
                <rect x="290" y="25" rx="3" ry="3" width="20" height="20" />
            </>
        ) : (
            <>
                {/* Desktop Layout */}
                {/* Status */}
                <rect x="0" y="20" rx="5" ry="20" width="80" height="24" />
                <rect x="0" y="50" rx="3" ry="3" width="100" height="10" />
                
                {/* Content */}
                <rect x="140" y="15" rx="3" ry="3" width="180" height="14" />
                <rect x="140" y="40" rx="3" ry="3" width="300" height="10" />
                <rect x="140" y="55" rx="3" ry="3" width="250" height="10" />
                <rect x="140" y="70" rx="3" ry="3" width="250" height="10" />
                
                {/* Date - removed */}
                
                {/* Media Thumbnail */}
                <rect x="600" y="15" rx="6" ry="6" width="64" height="64" />
                
                {/* Actions */}
                <rect x="700" y="30" rx="3" ry="3" width="30" height="30" />
            </>
        )}
    </ContentLoader>
    );
};

const IconForSocialMedia = ({ socialMedia }) => {
    const social = socialMedia.socialMedia || socialMedia;
    switch (social) {
        case 'youtube':
            return <BsYoutube color='red' size={13} />;
        case 'instagram':
            return <BsInstagram color='#C13584' size={13} />;
        case 'tiktok':
            return <BsTiktok color='black' size={13} />;
        case 'facebook':
            return <BsFacebook color='blue' size={13} />;
        case 'x':
            return <BsTwitterX color='black' size={13} />;
        case 'threads':
            return <BsThreads color='black' size={13} />;
        case 'linkedin':
            return <BsLinkedin color='black' size={13} />;
        case 'reddit':
            return <BsReddit color='black' size={13} />;
        case 'pinterest':
            return <BsPinterest color='black' size={13} />;
        default:
            return null; // Handle other cases or return a default icon

    }
};

const getIconForTab = (tabId) => {
    switch (tabId) {
        case 'failed':
            return <RiDeleteBinLine className="w-4 h-4" />;
        case 'draft':
            return <RiPencilLine className="w-4 h-4" />;
        case 'scheduled':
            return <RiPencilLine className="w-4 h-4" />;
        case 'uploaded':
            return <RiPencilLine className="w-4 h-4" />;
        default:
            return null;
    }
};

export default Drafts;
